import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import topicsData from "../data/topics";
import "./Topics.css";

// スクロールアニメーション用フック
function useIntersectionObserver(options = {}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  // optionsオブジェクトをRefで保持することで再レンダー時の参照変化を防ぐ
  const optionsRef = useRef(options);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, { threshold: 0.1, ...optionsRef.current });

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []); // optionsRefは安定した参照なので依存配列不要

  return [ref, isVisible];
}

// カード個別コンポーネント
function TopicCard({ topic, index }) {
  const [ref, isVisible] = useIntersectionObserver();
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    const rect = cardRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) / (rect.width / 2);
    const dy = (e.clientY - cy) / (rect.height / 2);
    setTilt({ x: dy * -6, y: dx * 6 });
  };

  const handleMouseLeave = () => setTilt({ x: 0, y: 0 });

  const formattedDate = new Date(topic.date).toLocaleDateString("ja-JP", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div
      ref={ref}
      className={`topics-card-wrapper ${isVisible ? "is-visible" : ""}`}
      style={{ "--delay": `${index * 0.08}s` }}
    >
      <Link
        to={`/topics/${topic.id}`}
        className="topics-card-link"
        aria-label={`${topic.title}の詳細を見る`}
      >
        <div
          ref={cardRef}
          className="topics-card"
          style={{
            transform: `perspective(800px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
          }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          {/* サムネイル */}
          <div className="topics-thumb-wrap">
            <img src={topic.thumbnail} alt={topic.title} className="topics-thumb" />
            <div className="topics-thumb-overlay" />
            <span className="topics-card-number">#{String(topic.id).padStart(2, "0")}</span>
          </div>

          {/* 本文エリア */}
          <div className="topics-card-body">
            <time className="topics-date" dateTime={topic.date}>{formattedDate}</time>
            <h3 className="topics-title">{topic.title}</h3>
            <p className="topics-summary">{topic.summary}</p>
            <span className="topics-read-more">
              詳細を見る
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </span>
          </div>

          {/* ホバー時のグロー */}
          <div className="topics-card-glow" />
        </div>
      </Link>
    </div>
  );
}

export default function TopicsList() {
  const [headerRef, headerVisible] = useIntersectionObserver();

  return (
    <div className="topics-page">
      <div id="page-top" />

      {/* ── ヘッダーセクション ── */}
      <div ref={headerRef} className={`topics-hero ${headerVisible ? "is-visible" : ""}`}>
        <div className="topics-hero-bg">
          <div className="hero-grid" />
        </div>
        <div className="topics-hero-content">
          <p className="topics-hero-eyebrow">ZENKYO NEWS</p>
          <h1 className="topics-hero-title">
            <span>ト</span><span>ピ</span><span>ッ</span><span>ク</span><span>ス</span>
          </h1>
          <p className="topics-hero-sub">社内の最新情報・イベント・お知らせをお届けします</p>
          <div className="topics-hero-line" />
        </div>
        <div className="topics-hero-count">
          <span>{topicsData.length}</span>
          <small>記事</small>
        </div>
      </div>

      {/* ── カードグリッド ── */}
      <div className="topics-grid-section">
        <div className="topics-grid">
          {topicsData.map((topic, index) => (
            <TopicCard key={topic.id} topic={topic} index={index} />
          ))}
        </div>
      </div>
    </div>
  );
}
